import { metaReducers, reducers } from 'app/reducer';
import { environment } from 'environments/environment';
import { WebStorageModule } from 'ngx-store';

import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { AppLocalizationService } from '@app/shared/common/localization/app-localization.service';
import { MessageService } from '@app/shared/common/services/message.service';
import { WINDOW_PROVIDERS } from '@app/shared/common/services/window.service';
import {
    MaintenanceNoticesService
} from '@app/shared/layout/active-maintenance-notices/maintenance-notices.service';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AppConsts } from '@shared/AppConsts';
import { AbpProxyModule } from '@shared/common/abp-module-proxy';
import { CommonModule } from '@shared/common/common.module';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { FS_API_BASE_URL } from '@shared/service-proxies/file-submission-service-proxies';
import {
    FileSubmissionServiceProxyModule
} from '@shared/service-proxies/file-submission-service-proxy.module';
import { API_BASE_URL, WebsiteAlertServiceProxy } from '@shared/service-proxies/service-proxies';
import { ServiceProxyModule } from '@shared/service-proxies/service-proxy.module';
import { TASK_API_BASE_URL } from '@shared/service-proxies/task-service-proxies';
import { TaskServiceProxyModule } from '@shared/service-proxies/task-service-proxy.module';
import { CATALIST_HTTP_PROVIDER } from '@shared/utils/correlator/http.provider';
import { UtilsModule } from '@shared/utils/utils.module';

import { AppPreBootstrap } from './AppPreBootstrap';
import { CustomPreloadingStrategy } from './custom-preloading-strategy';
import { RootEffects } from './effects';
import { RootRoutingModule } from './root-routing.module';
// import { PRODUCT_API_BASE_URL } from '@shared/service-proxies/product-service-proxies';
import { RootComponent } from './root.component';
import { FeatureManagerService } from './shared/feature-management/feature-manager.service';

export function appInitializerFactory(injector: Injector) {
    return () => {
        abp.ui.setBusy();

        handleLogoutRequest(injector.get(AppAuthService));

        return new Promise<boolean>((resolve, reject) => {
            AppPreBootstrap.run(() => {
                const appSessionService: AppSessionService = injector.get(AppSessionService);
                appSessionService.init().then(
                    (result) => {

                        // Css classes based on the layout
                        if (abp.session.userId) {
                            $('body').attr('class', 'page-md page-header-fixed page-sidebar-closed-hide-logo');
                        } else {
                            $('body').attr('class', 'page-md login');
                        }

                        // tenant specific custom css
                        if (appSessionService.tenant && appSessionService.tenant.customCssId) {
                            $('head').append(
                                '<link id="TenantCustomCss" href="'
                                + AppConsts.remoteServiceBaseUrl
                                + '/TenantCustomization/GetCustomCss?id='
                                + appSessionService.tenant.customCssId
                                + '" rel="stylesheet"/>'
                            );
                        }

                        const featureManagerService = injector.get(FeatureManagerService);
                        featureManagerService.initialize()
                            .then(() => {
                                abp.ui.clearBusy();
                                resolve(result);
                            });
                    },
                    (err) => {
                        abp.ui.clearBusy();
                        reject(err);
                    }
                );
            });
        });
    }
}

export function getRemoteServiceBaseUrl(): string {
    return AppConsts.remoteServiceBaseUrl;
}

export function getFSRemoteServiceBaseUrl(): string {
    return AppConsts.fileSubmissionSvcUrl;
}

export function getTaskRemoteServiceBaseUrl(): string {
    return AppConsts.schemaApi.task_service;
}

// export function getProductRemoteServiceBaseUrl(): string {
//     return `${AppConsts.schemaApi.gatewayUrl}/products`;
// }

export function getNotificationServiceUrl(): string {
    return AppConsts.notificationServiceUrl;
}

function handleLogoutRequest(authService: AppAuthService) {
    const currentUrl = UrlHelper.initialUrl;
    const returnUrl = UrlHelper.getReturnUrl();
    if (currentUrl.indexOf(('account/logout')) >= 0 && returnUrl) {
        authService.logout(true, returnUrl);
    }
}

@NgModule({
    imports: [
        HttpClientModule,
        BrowserModule,
        BrowserAnimationsModule,
        WebStorageModule,
        CommonModule.forRoot(),
        UtilsModule,

        // Doesn't help. Providers aren't seen, for example: MessageService
        AbpProxyModule.forRoot(),
        ServiceProxyModule,
        FileSubmissionServiceProxyModule,
        TaskServiceProxyModule,

        RootRoutingModule,

        /**
         * StoreModule.forRoot is imported once in the root module, accepting a reducer
         * function or object map of reducer functions. If passed an object of
         * reducers, combineReducers will be run creating your application
         * meta-reducer. This returns all providers for an @ngrx/store
         * based application.
         */
        StoreModule.forRoot(reducers, { metaReducers }),

        /**
         * After stage testing set logOnly for production environment
         * logOnly: environment.production
         */
        StoreDevtoolsModule.instrument({
            name: 'Catalyst Angular Core',
            maxAge: 50
        }),

        /**
         * @ngrx/router-store keeps router state up-to-date in the store.
         */
        StoreRouterConnectingModule.forRoot(),

        /**
         * EffectsModule.forRoot() is imported once in the root module and
         * sets up the effects class to be initialized immediately when the
         * application starts.
         *
         * See: https://github.com/ngrx/platform/blob/master/docs/effects/api.md#forroot
         */
        EffectsModule.forRoot([RootEffects])
    ],
    declarations: [
        RootComponent
    ],
    providers: [
        CATALIST_HTTP_PROVIDER,
        WINDOW_PROVIDERS,
        { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
        { provide: FS_API_BASE_URL, useFactory: getFSRemoteServiceBaseUrl },
        { provide: TASK_API_BASE_URL, useFactory: getTaskRemoteServiceBaseUrl },
        // { provide: PRODUCT_API_BASE_URL, useFactory: getProductRemoteServiceBaseUrl },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [Injector],
            multi: true
        },
        CustomPreloadingStrategy,
        AppAuthService,
        MessageService,
        AppLocalizationService,
        WebsiteAlertServiceProxy,
        MaintenanceNoticesService,
        { provide: MAT_DIALOG_DATA, useValue: {} }
    ],
    bootstrap: [RootComponent]
})
export class RootModule { }
